(() => {
  if (customElements.get('product-upsell-express')) return;

  const CONFIG_SELECTOR = 'cart-drawer [data-express-upsell-config]';
  const PREPARING = 'Preparing express checkout…';

  class ProductUpsellExpress extends HTMLElement {
    constructor() {
      super();
      this.sync = this.sync.bind(this);
      this.scheduleSync = this.scheduleSync.bind(this);
      this.onProductSelect = this.onProductSelect.bind(this);
      this.onWalletClick = this.onWalletClick.bind(this);
      this.onBaseOptionChange = this.onBaseOptionChange.bind(this);
      this.onBaseRequestFinished = this.onBaseRequestFinished.bind(this);
    }

    connectedCallback() {
      this.form = this.closest('form');
      this.productScope = this.closest('product-info, .shopify-section, dialog, product-card') || this.form;
      this.form?.addEventListener('input', this.sync);
      this.form?.addEventListener('change', this.sync);
      this.addEventListener('click', this.onWalletClick, true);
      this.productScope?.addEventListener('shopify:product:select', this.onProductSelect);
      this.productScope?.addEventListener('product-info:updated', this.onBaseRequestFinished);
      this.productScope?.addEventListener('product-info:request-error', this.onBaseRequestFinished);
      // The base also supports its own option bus when StandardEvents is absent.
      this.unsubscribeOptions = window.subscribe?.(
        window.PUB_SUB_EVENTS?.optionValueSelectionChange || 'option-value-selection-change',
        this.onBaseOptionChange
      );
      // Quantity inputs may be outside the form, linked by their form attribute.
      this.productScope?.addEventListener('input', this.scheduleSync);
      this.productScope?.addEventListener('change', this.scheduleSync);
      document.addEventListener('DOMContentLoaded', this.sync);
      document.addEventListener('shopify:section:load', this.scheduleSync);
      window.addEventListener('pageshow', this.scheduleSync);

      this.observer = new MutationObserver((records) => {
        // Observe server-rendered configuration/section replacement, never use
        // the cart's mutable data-selected or data-id as the payment selection.
        if (records.some((record) => !this.contains(record.target))) this.scheduleSync();
      });
      this.observer.observe(document.body, {
        childList: true, subtree: true, characterData: true, attributes: true,
        attributeFilter: ['value', 'disabled', 'hidden', 'data-express-addons'],
      });
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.form?.removeEventListener('input', this.sync);
      this.form?.removeEventListener('change', this.sync);
      this.removeEventListener('click', this.onWalletClick, true);
      this.productScope?.removeEventListener('shopify:product:select', this.onProductSelect);
      this.productScope?.removeEventListener('product-info:updated', this.onBaseRequestFinished);
      this.productScope?.removeEventListener('product-info:request-error', this.onBaseRequestFinished);
      this.unsubscribeOptions?.();
      this.productScope?.removeEventListener('input', this.scheduleSync);
      this.productScope?.removeEventListener('change', this.scheduleSync);
      document.removeEventListener('DOMContentLoaded', this.sync);
      document.removeEventListener('shopify:section:load', this.scheduleSync);
      window.removeEventListener('pageshow', this.scheduleSync);
      this.variantPromise = null;
      this.baseVariantPending = false;
    }

    scheduleSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      queueMicrotask(() => {
        this.syncQueued = false;
        if (this.isConnected) this.sync();
      });
    }

    onBaseOptionChange({ data } = {}) {
      if (data?.event?.target?.closest?.('product-info') !== this.productScope) return;
      this.baseVariantPending = true;
      this.variantError = '';
      this.scheduleSync();
    }

    onBaseRequestFinished(event) {
      if (event.target !== this.productScope) return;
      this.baseVariantPending = false;
      this.variantError = event.type === 'product-info:request-error'
        ? 'Unable to prepare this product option. Please select it again.' : '';
      this.scheduleSync();
    }

    onProductSelect(event) {
      const sourceCard = event.target.closest?.('product-card');
      if (sourceCard && !sourceCard.contains(this)) return;
      if (!event.promise?.then) return;

      const pending = event.promise;
      this.variantPromise = pending;
      this.variantError = '';
      this.sync();
      pending.then((result) => {
        if (!this.isConnected || this.variantPromise !== pending) return;
        this.variantError = result?.variant === null || result?.variant?.availableForSale === false
          || result?.detail?.resource?.available === false
          ? 'Select an available product option.' : '';
        this.variantPromise = null;
        // The product form resolves the same event and updates its hidden id.
        this.scheduleSync();
      }, () => {
        if (!this.isConnected || this.variantPromise !== pending) return;
        this.variantPromise = null;
        this.variantError = 'Unable to prepare this product option. Please select it again.';
        this.sync();
      });
    }

    getSelection() {
      if (document.readyState === 'loading' || this.variantPromise || this.baseVariantPending) throw new Error(PREPARING);
      if (this.variantError) throw new Error(this.variantError);

      const mainId = this.form?.querySelector('[name="id"]:not(:disabled)')?.value;
      if (!/^[1-9]\d*$/.test(mainId || '') || this.closest('.accelerated-checkout-block[hidden]')) {
        throw new Error('Select an available product option.');
      }
      if (this.form?.querySelector('button[name="add"]')?.disabled) {
        throw new Error('Select an available product option.');
      }
      const quantityInput = Array.from(this.form?.elements || [])
        .find((input) => input.name === 'quantity' && !input.disabled);
      const quantity = Number(quantityInput?.value ?? 1);
      if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Enter a valid quantity.');

      const items = [];
      if (this.form?.closest('product-form')?.dataset.expressAddons !== 'true') {
        return { mainId, quantity, items };
      }
      const seen = new Set();
      for (const item of this.form?.closest('product-form')?.getSelectedAdditionalItems?.() || []) {
        const id = String(item.id || '');
        if (id === mainId || seen.has(id)) continue;
        seen.add(id);
        items.push({ id, key: item.key, plan: item.sellingPlan || '', properties: item.properties, label: item.label || 'Add-on', price: '' });
      }
      for (const configNode of document.querySelectorAll(CONFIG_SELECTOR)) {
        let config;
        try {
          config = JSON.parse(configNode.textContent);
        } catch (_) {
          throw new Error('Unable to read the included add-ons. Please refresh the page.');
        }
        if (config?.version !== 1 || !Array.isArray(config.items)) {
          throw new Error('Unable to read the included add-ons. Please refresh the page.');
        }
        for (const item of config.items) {
          if (item.preselected !== true) continue;
          const id = String(item.id || '');
          if (!/^[1-9]\d*$/.test(id) || item.available !== true) {
            throw new Error('An included add-on is unavailable. Please contact the store before checkout.');
          }
          if (id === mainId || seen.has(id)) continue;
          if (!item.key || typeof item.key !== 'string') {
            throw new Error('An included add-on is missing its configuration. Please refresh the page.');
          }
          const plan = String(item.plan || '');
          if ((plan && !/^[1-9]\d*$/.test(plan)) || (item.requiresPlan === true && !plan)) {
            throw new Error('An included add-on has no available purchase plan.');
          }
          seen.add(id);
          items.push({ id, key: item.key, plan, label: String(item.label || 'Add-on'),
            price: String(item.price || '') });
        }
      }
      return { mainId, quantity, items };
    }

    onWalletClick(event) {
      if (!this.querySelector('[data-product-wallet]')?.contains(event.target)) return;
      // A ready click remains entirely native. Never add, clear, restore, or
      // submit the storefront cart on behalf of a product wallet.
      if (!this.sync()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    sync() {
      const wallet = this.querySelector('[data-product-wallet]');
      const fields = this.querySelector('[data-express-fields]');
      const status = this.querySelector('[data-product-express-status]');
      if (!wallet || !fields || !status) return false;
      try {
        const { mainId, items } = this.getSelection();
        const signature = JSON.stringify([mainId, items]);
        const currentFields = JSON.stringify(Array.from(fields.querySelectorAll('input'),
          (input) => [input.name, input.value]));
        if (signature !== this.signature || currentFields !== this.fieldsSnapshot) {
          const fragment = document.createDocumentFragment();
          const append = (name, value) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            input.dataset.expressAdditionalItem = 'true';
            fragment.append(input);
          };
          items.forEach((item, index) => {
            const prefix = `shopify-additional-items[${index + 1}]`;
            append(`${prefix}[id]`, item.id);
            append(`${prefix}[quantity]`, '1');
            append(`${prefix}[parent_id]`, mainId);
            // Shopify expects one JSON key/value array, not nested properties inputs.
            append(`${prefix}[properties]`, JSON.stringify(Object.entries(item.properties || { _theme_upsell_key: item.key }).map(([key, value]) => ({ key, value: String(value) }))));
            if (item.plan) append(`${prefix}[selling_plan]`, item.plan);
          });
          fields.replaceChildren(fragment);
          this.signature = signature;
          this.fieldsSnapshot = JSON.stringify(Array.from(fields.querySelectorAll('input'),
            (input) => [input.name, input.value]));
        }
        const message = items.length
          ? `Includes: ${items.map((item) => `${item.label}${item.price ? ` (${item.price})` : ''}`).join(', ')}`
          : '';
        if (status.textContent !== message) status.textContent = message;
        if (status.hidden !== !message) status.hidden = !message;
        if (wallet.inert) wallet.inert = false;
        return true;
      } catch (error) {
        if (!wallet.inert) wallet.inert = true;
        if (fields.childNodes.length) fields.replaceChildren();
        this.signature = null;
        this.fieldsSnapshot = null;
        if (status.hidden) status.hidden = false;
        if (status.textContent !== error.message) status.textContent = error.message;
        return false;
      }
    }
  }
  customElements.define('product-upsell-express', ProductUpsellExpress);
})();
